import { Alert, Center, Group, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconInbox } from '@tabler/icons-react';
import { ApiRequestError } from '../api/client';

/**
 * One component for loading, error, and empty.
 *
 * Every table in this dashboard has the same three non-happy states, and letting
 * each page invent its own is how a console ends up with five different empty
 * screens that each tell the operator something slightly different.
 */
export function DataState({
  loading,
  error,
  empty,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  action,
  children,
}: {
  loading?: boolean;
  error?: unknown;
  empty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    const message =
      error instanceof ApiRequestError ? error.message : 'Something went wrong loading this.';
    const details = error instanceof ApiRequestError ? error.fieldErrors() : {};

    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Could not load">
        <Stack gap={4}>
          <Text size="sm">{message}</Text>
          {Object.entries(details).map(([field, detail]) => (
            <Text key={field} size="xs" c="dimmed">
              {field}: {detail}
            </Text>
          ))}
        </Stack>
      </Alert>
    );
  }

  if (empty) {
    return (
      <Center py="xl">
        <Stack align="center" gap="xs" maw={420}>
          <ThemeIcon variant="light" size="lg" color="gray" radius="xl">
            <IconInbox size={18} />
          </ThemeIcon>
          <Text fw={500}>{emptyTitle}</Text>
          {emptyDescription && (
            <Text size="sm" c="dimmed" ta="center">
              {emptyDescription}
            </Text>
          )}
          {action && <Group mt="xs">{action}</Group>}
        </Stack>
      </Center>
    );
  }

  return <>{children}</>;
}
